import * as go from 'gojs';
import { store } from 'createStore';
import {
  setShowConnectionContextMenu,
  setShowUnitContextMenu,
} from 'store/contextmenu/reducer';
import {
  clearSelectedNodes,
  selectNode,
  setActiveUnit,
  setSelectedNodes,
} from '../../store/units/reducer';
import type { EditMultipleUnitLocationsInterface } from '../../store/units/types';
import { PanelType } from '../../store/units/types';
import { UnitTypes } from 'unitData/types';
import { updateConnectionPoints } from '../../store/connections/sagaActions';
import type { ConnectionPointsInterface } from '../../store/connections/types';
import {
  editUnitInfo,
  editMultipleUnitLocations,
} from 'store/units/sagaActions';
import { setOpenPanel, setShowSideCard } from 'store/layout/reducer';
import { isCanvasModalOpen } from './canvasModalLock';

export const registerDiagramListeners = (diagram: go.Diagram) => {
  diagram.addDiagramListener('ObjectContextClicked', (event) => {
    if (isCanvasModalOpen(diagram)) {
      return;
    }

    store.dispatch(setShowUnitContextMenu(null));
    const part = event.subject.part;
    if (part instanceof go.Node) {
      store.dispatch(setShowUnitContextMenu(event.subject.part.key));
    } else if (part instanceof go.Link) {
      store.dispatch(setShowConnectionContextMenu(event.subject.part.key));
    }
  });

  diagram.addDiagramListener('ChangedSelection', () => {
    if (isCanvasModalOpen(diagram)) {
      return;
    }

    if (diagram.toolManager.dragSelectingTool.isActive) {
      store.dispatch(clearSelectedNodes());
      const newSelection: string[] = [];
      const selection = diagram.selection;
      selection.each((part) => {
        if (part instanceof go.Node) {
          const nodeKey: string = part.data.isPanel
            ? `${part.data.unitKey}-${part.data.category}`
            : part.data.key;
          newSelection.push(nodeKey);
        }
      });
      store.dispatch(setSelectedNodes(newSelection));
    }
  });

  diagram.addDiagramListener('ObjectSingleClicked', function (event) {
    if (isCanvasModalOpen(diagram)) {
      return;
    }

    store.dispatch(setShowUnitContextMenu(null));
    const part = event.subject.part;
    const tool = diagram.toolManager.resizingTool;

    if (part instanceof go.Node) {
      if (part.data.category === UnitTypes.FREE_TEXT_BOX) {
        tool.computeResize = function (
          newPoint,
          spot,
          min,
          max,
          cell,
          _reshape,
        ) {
          return go.ResizingTool.prototype.computeResize.call(
            tool,
            newPoint,
            spot,
            min,
            max,
            cell,
            true,
          );
        };
      } else if (part.data.category === UnitTypes.PRINT_WINDOW) {
        tool.computeResize = function (
          newPoint,
          spot,
          min,
          max,
          cell,
          _reshape,
        ) {
          return go.ResizingTool.prototype.computeResize.call(
            tool,
            newPoint,
            spot,
            min,
            max,
            cell,
            false,
          );
        };
      }
      if (part.data.category !== UnitTypes.PRINT_WINDOW && part.data.key) {
        let unitKey: string = part.data.key;
        let nodeKey: string = part.data.key;
        if (part.data.isPanel) {
          unitKey = part.data.unitKey;
          nodeKey = `${unitKey}-${part.data.category}`;
        } else if (part.data.category === UnitTypes.HIGHLIGHT_TEXT_BOX) {
          const groups =
            store.getState().unitConnGroup.present.unitState.groups;
          const group = Object.values(groups).find((g) =>
            g.unitKeys.includes(unitKey),
          );
          if (!group) {
            return;
          }
          unitKey = group.key;
          nodeKey = `${unitKey}-${part.data.category}`;
        }

        store.dispatch(
          selectNode({
            unitKey,
            nodeKey,
            multiSelect:
              event.diagram.firstInput.meta || event.diagram.firstInput.control,
          }),
        );
      }
    }
  });

  diagram.addDiagramListener('BackgroundSingleClicked', function () {
    if (isCanvasModalOpen(diagram)) {
      return;
    }

    store.dispatch(setActiveUnit(null));
    store.dispatch(setOpenPanel(null));
    store.dispatch(setShowSideCard(false));
    store.dispatch(setShowUnitContextMenu(null));
    store.dispatch(clearSelectedNodes());
  });

  diagram.addDiagramListener('LinkReshaped', function (event) {
    const link = event.subject.part;
    if (link instanceof go.Link) {
      const connectionKey = link.key;
      const newPoints: go.List<go.Point> = link.points;
      const updatedPoints: ConnectionPointsInterface[] = [];
      for (let i = 0; i < newPoints.length; i++) {
        updatedPoints.push({
          x: newPoints.get(i).x,
          y: newPoints.get(i).y,
        });
      }
      if (connectionKey) {
        const connectionPointsPayload = {
          connectionKey: connectionKey.toString(),
          updateConnectionPoints: updatedPoints,
        };
        store.dispatch(updateConnectionPoints(connectionPointsPayload));
      }
    }
  });

  diagram.addDiagramListener('ClipboardPasted', (event) => {
    const goObjectSet = event.subject;
    const pastedObjs: any[] = goObjectSet.toArray();
    if (pastedObjs.length > 1) {
      store.dispatch(setActiveUnit(null));
      store.dispatch(
        setSelectedNodes(
          pastedObjs.flatMap((o) => [
            o.data.key,
            ...Object.values(PanelType).map((t) => `${o.data.key}-${t}`),
          ]),
        ),
      );
    } else {
      const pastedObj = pastedObjs[0];
      if (pastedObj) {
        store.dispatch(setActiveUnit(pastedObj.key.toString()));
      }
    }
  });

  diagram.addDiagramListener('PartRotated', function (event) {
    const node = event.subject.part;
    if (node instanceof go.Node) {
      const unit = store
        .getState()
        .unitConnGroup.present.unitState.units.find((u) => u.key === node.key);
      if (!unit) {
        return;
      }

      const newUnitInfo = {
        ...unit.unitInfo,
        angle: node.data.angle,
      };
      store.dispatch(editUnitInfo(node.data.key, newUnitInfo));
    }
  });

  diagram.addDiagramListener('SelectionMoved', function (_event) {
    diagram.skipsUndoManager = true;
    const it = diagram.selection.iterator;
    const locUpdates: EditMultipleUnitLocationsInterface[] = [];
    while (it.hasNext()) {
      const panelLocUpdates: PanelType[] = [];
      const node = it.value;
      if (
        node instanceof go.Node &&
        typeof node.data.key !== 'undefined' &&
        !node.data.isPanel
      ) {
        const panels = diagram.findNodesByExample({
          isPanel: true,
          unitKey: node.data.key,
        });
        const pIt = panels.iterator;
        while (pIt.hasNext()) {
          const panel = pIt.value;
          if (!panel.visible) {
            panelLocUpdates.push(panel.category as PanelType);
          }
        }
        locUpdates.push({
          unitKey: node.data.key,
          location: node.data.loc,
          panels: panelLocUpdates,
        });
      }
    }
    if (locUpdates.length > 0) {
      store.dispatch(editMultipleUnitLocations(locUpdates));
    }
  });
};
